<template>
  <div class="h-full bg-gray-900/50 flex flex-col">
    <!-- Compact Search Header -->
    <div class="bg-black/20 pl-1 mb-1">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Search..."
        class="w-full px-2 py-1 text-xs bg-transparent text-white placeholder-gray-400 focus:outline-hidden focus:border-blue-500"
      />
    </div>

    <!-- Sources Grid (RouterAlt Style) -->
    <div class="flex-1 overflow-y-auto grid grid-cols-[repeat(auto-fit,minmax(256px,1fr))] gap-3 p-2 content-start">
       <div
          v-for="source in filteredSources"
          :key="source.id"
          class="inline-block relative"
        >
          <!-- Source Item (Compact RouterAlt Style) -->
          <div
            class="relative grid grid-cols-[60px_1fr] gap-1 h-10 items-center overflow-hidden rounded-md cursor-pointer transition-all duration-200 group"
            :class="{
              'bg-slate-700': false,
              'bg-slate-800': !activeConnection || activeConnection.sourceId !== source.id,
              'ring-2 ring-orange-400': activeConnection && activeConnection.sourceId === source.id,
              'ring-1 ring-blue-400': selectedSource && selectedSource.id === source.id && (!activeConnection || activeConnection.sourceId !== source.id)
            }"
            @click="selectSource(source)"
          >
            <!-- Thumbnail Preview (Compact) -->
            <div class="relative h-8 w-14 flex ml-1 items-center justify-center bg-slate-900/50 rounded-r overflow-hidden">
              <!-- Thumbnail Image -->
              <img
                v-if="getThumbnailData(source).data && !getThumbnailData(source).error"
                :src="getThumbnailData(source).data"
                :alt="`${source.name} preview`"
                class="w-full h-full object-cover"
                :title="`Last updated: ${formatThumbnailTime(getThumbnailData(source).timestamp)}`"
              />
              
              <!-- Loading State -->
              <div v-else-if="getThumbnailData(source).loading" class="flex items-center justify-center text-blue-400">
                <svg class="w-2 h-2 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              
              <!-- Error State -->
              <div v-else-if="getThumbnailData(source).error" class="flex items-center justify-center text-red-400">
                <svg class="w-2 h-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              
              <!-- No Data State -->
              <div v-else class="flex items-center justify-center text-gray-500">
                <svg class="w-2 h-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              
              <!-- Active Connection Indicator -->
              <div 
                v-if="activeConnection && activeConnection.sourceId === source.id"
                class="absolute inset-0 bg-orange-500/30 flex items-center justify-center"
              >
                <div class="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse"></div>
              </div>
            </div>

            <!-- Content (Compact) -->
            <div class="min-w-0 relative px-1">
              <div 
                class="text-xs font-medium truncate"
                :class="{
                  'text-orange-300': activeConnection && activeConnection.sourceId === source.id,
                  'text-white': !activeConnection || activeConnection.sourceId !== source.id
                }"
              >
                {{ source.name }}
              </div>
              <div class="text-[10px] text-gray-400 truncate mt-0.5 flex items-center gap-1">
                <span
                  class="px-1 py-[1px] rounded-xs border border-slate-600/50 bg-slate-800/50 text-slate-300 leading-none"
                >
                  {{ source.sourceType || source.type || 'INPUT' }}
                </span>
                <span class="text-gray-500">•</span>
                <span v-if="activeConnection && activeConnection.sourceId === source.id" class="text-orange-400">
                  {{ activeConnection.audioChannel }}
                </span>
                <span v-else>
                  Click to connect
                </span>
              </div>
            </div>
          </div>

          <!-- Audio Channel Dropdown (Absolutely Positioned) -->
          <div 
            v-if="selectedSource && selectedSource.id === source.id && showAudioDropdown"
            class="absolute top-full left-0 mt-1 bg-gray-950 bg-blur-md rounded-xs overflow-hidden animate-slideDown z-50 shadow-xl"
          >
            <div class="p-2 border-b border-white/10 text-[10px] text-gray-300">
              Select Audio Channel:
            </div>
            <div class="max-h-24 overflow-y-auto">
              <button
                v-for="channel in audioChannels"
                :key="channel.id"
                @click="selectAudioChannel(source, channel.name)"
                class="w-full text-left px-2 py-1 text-xs text-white hover:bg-blue-600/20 transition-colors border-b border-gray-700 last:border-b-0"
                :class="{
                  'bg-blue-600/30 text-blue-300': activeConnection && activeConnection.sourceId === source.id && activeConnection.audioChannel === channel.name
                }"
              >
                {{ channel.name }}
              </button>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="filteredSources.length === 0" class="text-center text-gray-500 mt-8">
          <svg class="mx-auto h-6 w-6 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p class="text-xs">No sources found</p>
        </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, inject, onMounted, getCurrentInstance } from "vue";

// Props
const props = defineProps({
  rowId: {
    type: Number,
    required: true
  },
  inputSources: {
    type: Array,
    default: () => []
  },
  selectedSource: {
    type: Object,
    default: null
  },
  activeConnection: {
    type: Object,
    default: null
  },
  thumbnailProvider: {
    type: Object,
    default: () => ({})
  }
});

// Emits
const emit = defineEmits(['source-selected', 'audio-channel-selected']);

// Get access to the Vue instance for thumbnail functionality
const instance = getCurrentInstance();

// Reactive state
const searchQuery = ref("");
const showAudioDropdown = ref(false);

// Audio channel options - pairs that map to URL parameters
const audioChannels = ref([
  { id: 1, name: "Pair 1 ", urlParam: "a1" },
  { id: 2, name: "Pair 2 ", urlParam: "a2" },
  { id: 3, name: "Pair 3 ", urlParam: "a3" },
  { id: 4, name: "Pair 4 ", urlParam: "a4" },
  { id: 5, name: "Pair 5 ", urlParam: "a5" },
  { id: 6, name: "Pair 6 ", urlParam: "a6" },
  { id: 7, name: "Pair 7 ", urlParam: "a7" },
  { id: 8, name: "Pair 8 ", urlParam: "a8" }
]);

// Thumbnail functionality - use passed provider or fallback
const getThumbnailData = (source) => {
  if (props.thumbnailProvider && props.thumbnailProvider.getThumbnailData) {
    return props.thumbnailProvider.getThumbnailData(source);
  }
  
  // Fallback if provider not available
  return {
    data: null,
    loading: false,
    error: null,
    timestamp: null
  };
};

const formatThumbnailTime = (timestamp) => {
  if (!timestamp) return 'Never';
  
  const now = new Date();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
};

// Computed
const filteredSources = computed(() => {
  let sources = [...props.inputSources]; // Create a copy to avoid mutating original
  
  // Filter by search query if provided
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase();
    sources = sources.filter(source => 
      source.name.toLowerCase().includes(query) ||
      (source.description && source.description.toLowerCase().includes(query))
    );
  }
  
  // Sort by name alphabetically (safe to mutate the copy)
  return sources.sort((a, b) => a.name.localeCompare(b.name));
});

// Methods
const selectSource = (source) => {
  console.log(`Source list (row ${props.rowId}): Source clicked:`, source.name);
  
  // If clicking the same source, toggle dropdown
  if (props.selectedSource && props.selectedSource.id === source.id) {
    showAudioDropdown.value = !showAudioDropdown.value;
  } else {
    // New source selected
    showAudioDropdown.value = true;
    emit('source-selected', source);
  }
};

const selectAudioChannel = (source, channelName) => {
  console.log(`Source list (row ${props.rowId}): Audio channel selected:`, channelName);
  
  // Find the selected channel to get its URL parameter
  const selectedChannel = audioChannels.value.find(ch => ch.name === channelName);
  const urlParam = selectedChannel ? selectedChannel.urlParam : 'a1';
  
  // Modify the source URL to include the audio parameter
  let modifiedSource = { ...source };
  if (source.url && source.url.includes('/whep')) {
    // Add the audio parameter to the WHEP URL
    modifiedSource.url = source.url.replace('/whep', `/${urlParam}/whep`);
    console.log(`Modified URL for ${channelName} (${urlParam}):`, modifiedSource.url);
  }
  
  showAudioDropdown.value = false;
  emit('audio-channel-selected', modifiedSource, channelName);
};

// Watch for changes in selectedSource from parent
const watchSelectedSource = () => {
  if (!props.selectedSource) {
    showAudioDropdown.value = false;
  }
};

onMounted(() => {
  watchSelectedSource();
});
</script>

<style scoped>
/* Smooth dropdown animation */
@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    max-height: 200px;
    transform: translateY(0);
  }
}

.animate-slideDown {
  animation: slideDown 0.3s ease-out;
}

/* Custom scrollbar for source list */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: rgba(55, 65, 81, 0.5);
}

::-webkit-scrollbar-thumb {
  background: rgba(156, 163, 175, 0.5);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(156, 163, 175, 0.8);
}

/* Hover effects */
.group:hover .thumbnail-preview {
  transform: scale(1.05);
}

/* Connection pulse animation */
@keyframes connectionPulse {
  0%, 100% { 
    opacity: 1; 
    transform: scale(1);
  }
  50% { 
    opacity: 0.7; 
    transform: scale(0.9);
  }
}

.animate-pulse {
  animation: connectionPulse 2s infinite;
}

/* Source item transitions */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 200ms;
}

/* Focus styles for accessibility */
button:focus {
  outline: 2px solid rgba(59, 130, 246, 0.5);
  outline-offset: 2px;
}

input:focus {
  outline: none;
  box-shadow: 0 0 0 1px #3b82f6;
}
</style>
